import {
  defaultEventReportValues,
  EventReportForm,
  EventReportSchema,
  useNewEventReportMutation,
} from "@ac-expo/api";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Form,
  FormRadio,
  FormTextarea,
  Separator,
  toast,
} from "@ac-expo/ui-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { isAxiosError } from "axios";
import { useState } from "react";
import { Control, useForm } from "react-hook-form";

type EventReportDialogProps = {
  eventId: string;
  isEventReportModalOpen: boolean;
  setIsEventReportModalOpen: (open: boolean) => void;
};

export const EventReportDialog = ({
  eventId,
  isEventReportModalOpen,
  setIsEventReportModalOpen,
}: EventReportDialogProps) => {
  const [isConfirming, setIsConfirming] = useState(false);
  const form = useForm<EventReportForm>({
    resolver: zodResolver(EventReportSchema),
    defaultValues: defaultEventReportValues,
  });
  const { control, watch, handleSubmit, reset } = form;
  const selectedOption = watch("reportOption");

  const { mutate } = useNewEventReportMutation(eventId);

  const onSubmit = async (data: EventReportForm) => {
    mutate(data, {
      onSuccess: () => {
        reset(defaultEventReportValues);
        setIsEventReportModalOpen(false);
        setIsConfirming(false);

        toast({
          title: "Abus signalé",
          description:
            "Votre abus a bien été signalé. Nous vous examinerons le signalement au plus vite.",
        });
      },
      onError: (error) => {
        if (isAxiosError(error) && error.response?.status === 400) {
          toast({
            title: "Erreur",
            description: "Vous avez déjà signalé un abus pour cet événement",
          });
        } else {
          toast({
            title: "Erreur",
            description:
              "Une erreur inattendue est survenue, veuillez réessayer plus tard",
          });
        }
      },
    });
  };

  return (
    <Dialog
      open={isEventReportModalOpen}
      onOpenChange={(open) => {
        setIsEventReportModalOpen(open);
        setIsConfirming(false);
      }}
    >
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader className="py-1">
          <DialogTitle>
            {isConfirming && "Confirmation: "} Signaler l&apos;événement
          </DialogTitle>
          <p className="mt-4 text-sm font-light text-gray-600">
            {isConfirming
              ? "Confirmez votre signalement"
              : "Signaler l'événement auprès des équipes de Connect'Events"}
          </p>
        </DialogHeader>
        <Separator />
        <Form {...form}>
          <form
            onSubmit={(e) => {
              handleSubmit(onSubmit)(e);
              e.preventDefault();
            }}
            className="flex flex-col gap-3"
          >
            <FormRadio
              control={control}
              name="reportOption"
              containerClassName="flex flex-1 flex-col gap-4 rounded-xl bg-gray-50 p-4 pt-0"
              labelClassName="text-sm font-semibold text-gray-500 flex-1"
              itemClassName=" flex-row flex-row-reverse"
              itemContainerClassName="flex flex-col gap-4 border border-gray-200 p-4 rounded-xl"
              disabled={isConfirming}
              radioItems={[
                {
                  label: "Cet événement est faux",
                  value: "fakeEvent",
                  component: <span className="hidden" />,
                },
                {
                  label: "Cet événement est en double",
                  value: "duplicatedEvent",
                  component: <span className="hidden" />,
                },
                {
                  label: "Les informations de l’événement sont incorrectes",
                  value: "incorrectInformations",
                  component: <span className="hidden" />,
                },
                {
                  label: "Autre, précisez",
                  value: "other",
                  component: (
                    <div className="flex flex-col gap-2">
                      <FormTextarea
                        label=""
                        control={control as Control<EventReportForm>}
                        name="otherReason"
                        placeholder="Message"
                        hasMessage={false}
                        errorRing
                        maxLength={200}
                        disabled={selectedOption !== "other" || isConfirming}
                      />
                    </div>
                  ),
                },
              ]}
            />
            {isConfirming ? (
              <Button variant="blue" type="submit" className="ml-auto">
                Confirmer et envoyer
              </Button>
            ) : null}
          </form>

          {!isConfirming && (
            <Button
              variant="blue"
              onClick={() => setIsConfirming(true)}
              className="ml-auto"
            >
              Valider
            </Button>
          )}
        </Form>
      </DialogContent>
    </Dialog>
  );
};
